/**
 * pages/procurement/ContractDetailPage.jsx — per-type Contract Details.
 *
 * Modern entity-header strip (title + badges + KPI stats) over underline
 * tabs: Summary · Pricing (type-specific) · Performance · Documents.
 * Stays consistent with the BillPortal light enterprise aesthetic.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Pencil, MapPin, Building, Gauge, Clock, Calendar, FileText, Briefcase,
} from 'lucide-react';

import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { ContractDrawer } from '../../components/procurement/ContractDrawer';
import { PricingSection } from '../../components/procurement/PricingSection';
import { PerformanceSection } from '../../components/procurement/PerformanceSection';
import { DocumentsSection } from '../../components/procurement/DocumentsSection';
import { getContract, CONTRACT_TYPES, COMMODITIES } from '../../api/contracts';
import { getCurrentAccount, isSuperAdmin } from '../../lib/auth';

const STATUS_VARIANT = { active: 'default', expired: 'destructive', upcoming: 'secondary', draft: 'outline' };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const labelOf = (list, v) => list.find((x) => x.value === v)?.label || v || '—';

const TAB_TRIGGER =
  'rounded-none border-b-2 border-transparent bg-transparent px-3 pb-2.5 pt-1 -mb-px font-medium text-muted-foreground shadow-none ' +
  'data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none transition-colors';

/** Header KPI cell. */
const HeaderStat = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-start gap-3 p-4">
    <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-muted-foreground" />
    </div>
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold truncate ${accent || ''}`}>{value || '—'}</div>
    </div>
  </div>
);

/** Labeled stat tile used inside summary cards. */
const StatBlock = ({ label, value, full, mono, testId }) => (
  <div className={`rounded-md border bg-muted/30 px-3 py-2.5 hover:bg-muted/60 transition-colors ${full ? 'col-span-2' : ''}`} data-testid={testId}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`text-sm font-semibold mt-0.5 break-words ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
  </div>
);

/** Bordered card with a titled header bar. */
const SectionCard = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`rounded-lg border bg-card ${className}`}>
    <div className="flex items-center gap-2 px-4 py-3 border-b text-sm font-semibold">
      {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      {title}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const SiteGroup = ({ icon: Icon, label, items, render }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <Badge variant="secondary" className="text-[10px] tabular-nums">{items.length}</Badge>
    </div>
    {items.length === 0
      ? <p className="text-sm text-muted-foreground">None linked</p>
      : <ul className="space-y-1.5">{items.map(render)}</ul>}
  </div>
);

export default function ContractDetailPage() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const account = getCurrentAccount();
  const accountId = account?.id;
  const canEdit = isSuperAdmin() || ['admin', 'owner'].includes(account?.role);

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const data = await getContract({ accountId, id: contractId });
      setContract(data);
    } catch (e) {
      toast.error('Failed to load contract', { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [accountId, contractId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <MainLayout><div className="max-w-[1200px] mx-auto space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div></MainLayout>;
  }
  if (!contract) {
    return <MainLayout><div className="max-w-[1200px] mx-auto py-20 text-center text-muted-foreground">Contract not found.</div></MainLayout>;
  }

  const links = contract.links || {};
  const sitesCovered = (links.properties || []).length + (links.accounts || []).length;
  const expiring = contract.days_to_expiry !== null && contract.status !== 'expired';

  return (
    <MainLayout>
      <div className="max-w-[1200px] mx-auto space-y-6">
        {/* Entity header strip */}
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="p-5 sm:p-6">
            <button onClick={() => navigate('/procurement/contracts')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3 transition-colors" data-testid="contract-back-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> Contracts
            </button>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{contract.name}</h1>
                <div className="flex items-center gap-2 flex-wrap mt-2.5">
                  <Badge variant={STATUS_VARIANT[contract.status] || 'outline'} className="capitalize">{contract.status}</Badge>
                  <Badge variant="outline">{labelOf(CONTRACT_TYPES, contract.contract_type)}</Badge>
                  <Badge variant="outline" className="capitalize">{labelOf(COMMODITIES, contract.commodity)}</Badge>
                  {expiring && (
                    <span className={`text-xs flex items-center gap-1 ${contract.days_to_expiry <= 90 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      <Clock className="w-3.5 h-3.5" />{contract.days_to_expiry} days to expiry
                    </span>
                  )}
                </div>
              </div>
              {canEdit && (
                <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="contract-edit-header-btn"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 border-t divide-x divide-border [&>*:nth-child(3)]:border-l-0 md:[&>*:nth-child(3)]:border-l">
            <HeaderStat icon={Building} label="Vendor / Supplier" value={contract.vendor_name} />
            <HeaderStat icon={Calendar} label="Contract Term" value={`${fmtDate(contract.start_date)} → ${fmtDate(contract.end_date)}`} />
            <HeaderStat icon={Clock} label="Days to Expiry" value={expiring ? `${contract.days_to_expiry} days` : '—'} accent={expiring && contract.days_to_expiry <= 90 ? 'text-amber-600' : ''} />
            <HeaderStat icon={MapPin} label="Sites Covered" value={`${sitesCovered}`} />
          </div>
        </div>

        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="bg-transparent p-0 h-auto gap-1 border-b w-full justify-start rounded-none" data-testid="contract-detail-tabs">
            <TabsTrigger className={TAB_TRIGGER} value="summary" data-testid="tab-summary">Summary</TabsTrigger>
            <TabsTrigger className={TAB_TRIGGER} value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger className={TAB_TRIGGER} value="performance" data-testid="tab-performance">Performance</TabsTrigger>
            <TabsTrigger className={TAB_TRIGGER} value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          </TabsList>

          {/* Summary + Contracted Sites */}
          <TabsContent value="summary" className="mt-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 space-y-5">
                <SectionCard title="Contract Details" icon={FileText}>
                  <div className="grid grid-cols-2 gap-3">
                    <StatBlock label="Vendor / Supplier" value={contract.vendor_name} />
                    <StatBlock label="Contract Owner" value={contract.contract_owner} />
                    <StatBlock label="Start Date" value={fmtDate(contract.start_date)} />
                    <StatBlock label="End Date" value={fmtDate(contract.end_date)} />
                    <StatBlock label="Issue / Award Date" value={fmtDate(contract.issue_date)} />
                    <StatBlock label="Contract Group" value={contract.contract_group} />
                    <StatBlock label="T&D Tariff" value={contract.td_tariff} full />
                  </div>
                </SectionCard>

                <SectionCard title="Company & Legal" icon={Briefcase}>
                  <div className="grid grid-cols-2 gap-3">
                    <StatBlock label="Company Name" value={contract.company_name} />
                    <StatBlock label="EIN" value={contract.ein} mono />
                    <StatBlock label="Utility Account" value={contract.utility_account} mono />
                    <StatBlock label="Address" value={contract.company_address} full />
                  </div>
                </SectionCard>
              </div>

              <SectionCard title="Contracted Sites" icon={MapPin} className="self-start">
                <div className="space-y-5">
                  <SiteGroup
                    icon={MapPin} label="Properties" items={links.properties || []}
                    render={(p) => <li key={p.id} className="text-sm" data-testid={`linked-property-${p.id}`}>{p.name}{p.city ? ` · ${p.city}, ${p.state || ''}` : ''}</li>}
                  />
                  <div className="border-t pt-4">
                    <SiteGroup
                      icon={Building} label="Utility Accounts" items={links.accounts || []}
                      render={(a) => <li key={a.id} className="text-sm font-mono" data-testid={`linked-account-${a.id}`}>{a.account_number} · {a.vendor_name}</li>}
                    />
                  </div>
                  <div className="border-t pt-4">
                    <SiteGroup
                      icon={Gauge} label="Meters" items={links.meters || []}
                      render={(m) => <li key={m.id} className="text-sm font-mono" data-testid={`linked-meter-${m.id}`}>{m.meter_number} · {m.commodity}</li>}
                    />
                  </div>
                </div>
              </SectionCard>
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="mt-5">
            <div className="rounded-lg border bg-card p-5">
              <PricingSection accountId={accountId} contract={contract} canEdit={canEdit} onChanged={load} />
            </div>
          </TabsContent>

          <TabsContent value="performance" className="mt-5">
            <div className="rounded-lg border bg-card p-5">
              <PerformanceSection accountId={accountId} contract={contract} canEdit={canEdit} onChanged={load} />
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-5">
            <div className="rounded-lg border bg-card p-5">
              <DocumentsSection accountId={accountId} contract={contract} canEdit={canEdit} onChanged={load} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ContractDrawer open={editOpen} onOpenChange={setEditOpen} accountId={accountId} existing={contract} onSaved={load} />
    </MainLayout>
  );
}
